import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";

export default function DepositApprovalPlayers({ enabled }) {
  const [selected, setSelected] = useState([]);
  const [savedIds, setSavedIds] = useState([]);
  const [version, setVersion] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const apply = (data) => {
    const players = new Map((data.players || []).map(p => [p.id, p]));
    setSelected((data.user_ids || []).map(id => players.get(id) || { id, username: "Unavailable player" }));
    setSavedIds(data.user_ids || []); setVersion(data.version);
  };
  const reload = async () => {
    setLoading(true); setError(""); setMessage("");
    try { const { data } = await api.get("/admin/settings/deposit-auto-approval"); apply(data); }
    catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let active = true;
    api.get("/admin/settings/deposit-auto-approval").then(({ data }) => { if (active) apply(data); })
      .catch(e => { if (active) setError(formatApiError(e)); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/admin/settings/deposit-auto-approval/players", { params: { q: query } });
        if (active) setResults(data.players || []);
      } catch (e) { if (active) { setResults([]); setError(formatApiError(e)); } }
      finally { if (active) setSearching(false); }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [query]);
  const ids = selected.map(p => p.id);
  const dirty = [...ids].sort().join(",") !== [...savedIds].sort().join(",");
  const toggle = p => { setSelected(rows => rows.some(x => x.id === p.id) ? rows.filter(x => x.id !== p.id) : [...rows, p]); setMessage(""); };
  const save = async () => {
    setSaving(true); setError(""); setMessage("");
    try {
      const { data } = await api.put("/admin/settings/deposit-auto-approval", { user_ids: ids, expected_version: version });
      apply(data); setMessage("Deposit player selection saved.");
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <section className="mb-5 max-w-2xl rounded-lg border border-[#2F6FA5]/40 bg-[#101010] p-5 sm:p-6" aria-label="Deposit auto-approval players">
    <h2 className="text-lg font-semibold text-white">Players allowed for deposit auto-approval</h2>
    <p className="mt-2 text-sm leading-6 text-slate-300">Only selected players can have screenshot Chime/Venmo deposits approved automatically while the auto-approval switch is ON. Everyone else stays on manual review. Cashout settings are separate from this player list.</p>
    <p className="mt-2 text-sm text-amber-200">{enabled ? "Auto-approval switch is ON." : "Auto-approval switch is OFF. Saved players will apply when it is turned ON."}</p>
    <p className="mt-2 text-sm text-slate-300">{loading ? "Loading selection..." : `${selected.length} player${selected.length === 1 ? "" : "s"} selected`}{dirty ? " · Unsaved changes" : ""}</p>
    {!loading && selected.length === 0 && <p className="mt-2 text-sm text-slate-400">No players selected: screenshot deposits require manual review.</p>}
    <div className="mt-3 space-y-2">
      {selected.map(p => <div key={p.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-700 p-3">
        <span className="min-w-0 break-all text-sm text-white">{p.username}{p.full_name ? ` · ${p.full_name}` : ""}</span>
        <button type="button" aria-label={`Remove ${p.username}`} onClick={() => toggle(p)} disabled={saving} className="shrink-0 text-sm text-rose-300 disabled:opacity-50">Remove</button>
      </div>)}
    </div>
    <label className="mt-4 block text-sm text-slate-300" htmlFor="deposit-player-search">Find player by username, name or email</label>
    <input id="deposit-player-search" value={query} onChange={e => setQuery(e.target.value)} maxLength={80} disabled={loading || saving || version === null}
      className="mt-2 w-full rounded-lg border border-slate-600 bg-slate-900 px-3 py-3 text-white" placeholder="Search players" />
    <div className="mt-3 max-h-64 space-y-1 overflow-y-auto" aria-label="Player search results">
      {searching ? <p className="text-sm text-slate-400">Searching...</p> : results.map(p => <label key={p.id} className="flex cursor-pointer items-start gap-3 rounded-lg bg-slate-900 p-3 text-sm text-white">
        <input type="checkbox" checked={ids.includes(p.id)} onChange={() => toggle(p)} disabled={loading || saving || version === null} className="mt-1" />
        <span className="min-w-0 break-all">{p.username}{p.full_name ? ` · ${p.full_name}` : ""}</span>
      </label>)}
      {!searching && !results.length && <p className="text-sm text-slate-400">No matching players.</p>}
    </div>
    {error && <p role="alert" className="mt-3 text-sm text-rose-300">{error}</p>}
    {message && <p role="status" className="mt-3 text-sm text-emerald-300">{message}</p>}
    <div className="mt-4 flex flex-wrap gap-3">
      <button type="button" onClick={save} disabled={loading || saving || version === null || !dirty} className="rounded-full bg-[#2F6FA5] px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{saving ? "Saving..." : "Save player selection"}</button>
      <button type="button" onClick={reload} disabled={loading || saving} className="rounded-full border border-slate-600 px-4 py-3 text-sm text-slate-300 disabled:opacity-50">Reload saved selection</button>
    </div>
  </section>;
}
